import { useState, useCallback } from "react";
import {
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormClearErrors,
  FieldErrors,
} from "react-hook-form";
import { createProvider, updateProvider } from "services/paymentApi";
import type { ProviderAdditionalStep } from "services/paymentApi.types";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../../utils/errorHandler";
import { buildProviderPayload } from "../utils/providerPayloadBuilder";
import { createProviderObject } from "../utils/providerObjectBuilder";
import type { ConfigMode } from "../Context/types";
import { AvailableProvider, ExistingProvider, FormData } from "../types";
import type { Provider } from "../../../../DealerAcquirersTable/types";
import { filterSchemaBasedErrors } from "../utils/providerHelpers";
import {
  hasProviderAdditionalStep,
  normalizeProviderAdditionalStep,
} from "../utils/additionalStepContent";

export interface ProviderSubmissionSuccessMeta {
  additionalStepPresented?: boolean;
}

interface SubmissionConfig {
  mode: ConfigMode;
  dealerId: string;
  dealerProviders?: Provider[];
}

interface ProviderData {
  selectedProvider: AvailableProvider | null;
  existingProvider: ExistingProvider | null;
}

interface FormHandlers {
  getValues: UseFormGetValues<FormData>;
  handleSubmit: UseFormHandleSubmit<FormData>;
  setFormErrors: (errors: FieldErrors<FormData>) => void;
  clearErrors: UseFormClearErrors<FormData>;
}

interface ReviewState {
  showReview: boolean;
  setShowReview: (show: boolean) => void;
}

interface ModalHandlers {
  setShowConfirmSave: (show: boolean) => void;
  setShowSuccessMessage: (show: boolean) => void;
  setAdditionalStep: (step: ProviderAdditionalStep | null) => void;
  setIsLoading: (loading: boolean) => void;
}

interface SubmissionCallbacks {
  onReviewModeChange?: (isReview: boolean) => void;
  onSuccess?: (
    provider?: Provider,
    meta?: ProviderSubmissionSuccessMeta,
  ) => void;
  onProviderUpdated?: (provider?: Provider) => void;
}

interface UseProviderSubmissionProps {
  config: SubmissionConfig;
  providers: ProviderData;
  form: FormHandlers;
  review: ReviewState;
  modals: ModalHandlers;
  callbacks: SubmissionCallbacks;
}

interface EditSubmitParams {
  dealerId: string;
  selectedProvider: AvailableProvider;
  existingProvider: ExistingProvider;
  dealerProviders?: Provider[];
  getValues: UseFormGetValues<FormData>;
  showReview: boolean;
}

interface AddSubmitParams {
  dealerId: string;
  selectedProvider: AvailableProvider;
  getValues: UseFormGetValues<FormData>;
}

async function executeEditSubmit({
  dealerId,
  selectedProvider,
  existingProvider,
  dealerProviders,
  getValues,
  showReview,
}: EditSubmitParams): Promise<{ provider: Provider; message: string }> {
  const formData = getValues();
  const payload = buildProviderPayload(selectedProvider, formData);

  await updateProvider({
    merchantId: dealerId,
    providerId: existingProvider.id,
    data: payload,
  });

  const existingProviderData = dealerProviders?.find(
    (p) => p.id === existingProvider.id,
  );

  const updatedProvider = createProviderObject({
    id: existingProvider.id,
    selectedProvider,
    formData,
    payload,
    dealerId,
    createdAt: existingProviderData?.createdAt,
  });

  const successMessage = showReview
    ? "Edição do Provedor alterada com sucesso!"
    : "Provedor atualizado com sucesso!";

  return { provider: updatedProvider, message: successMessage };
}

async function executeAddSubmit({
  dealerId,
  selectedProvider,
  getValues,
}: AddSubmitParams): Promise<{
  provider: Provider;
  message: string;
  additionalStep?: ProviderAdditionalStep;
}> {
  const formData = getValues();
  const payload = buildProviderPayload(selectedProvider, formData);

  const response = await createProvider({
    merchantId: dealerId,
    data: payload,
  });

  const createdProvider = createProviderObject({
    id: response.id,
    selectedProvider,
    formData,
    payload,
    dealerId,
    useCredentialsFallback: true,
  });

  const normalizedAdditionalStep = response.additionalSteps
    ? normalizeProviderAdditionalStep(response.additionalSteps)
    : null;

  return {
    provider: {
      ...createdProvider,
      ...(normalizedAdditionalStep
        ? { additionalSteps: normalizedAdditionalStep }
        : {}),
    },
    message: "Provedor adicionado com sucesso!",
    additionalStep: normalizedAdditionalStep ?? undefined,
  };
}

function getErrorMessageForMode(mode: ConfigMode, showReview: boolean): string {
  if (mode === "EDIT") {
    return showReview
      ? "Falha ao tentar salvar edição. Tente novamente!"
      : "Não foi possível atualizar provedor. Tente novamente!";
  }
  return "Não foi possivel adicionar provedor. Tente novamente!";
}

export function useProviderSubmission({
  config,
  providers,
  form,
  review,
  modals,
  callbacks,
}: UseProviderSubmissionProps) {
  const { mode, dealerId, dealerProviders } = config;
  const { selectedProvider, existingProvider } = providers;
  const { getValues, handleSubmit, setFormErrors, clearErrors } = form;
  const { showReview, setShowReview } = review;
  const { setShowConfirmSave, setShowSuccessMessage, setIsLoading } = modals;
  const { onReviewModeChange, onSuccess, onProviderUpdated } = callbacks;

  const [isSaving, setIsSaving] = useState(false);

  const resetLoadingState = useCallback(() => {
    setIsLoading(false);
    setIsSaving(false);
    setShowConfirmSave(false);
  }, [setIsLoading, setShowConfirmSave]);

  const onSubmit = useCallback(async () => {
    if ((mode === "ADD" || mode === "EDIT") && !showReview) {
      setShowReview(true);
      onReviewModeChange?.(true);
      return;
    }

    setIsLoading(true);
    setIsSaving(true);

    try {
      if (mode === "EDIT") {
        if (!existingProvider || !dealerId || !selectedProvider) {
          resetLoadingState();
          return;
        }

        const { provider, message } = await executeEditSubmit({
          dealerId,
          selectedProvider,
          existingProvider,
          dealerProviders,
          getValues,
          showReview,
        });

        toast(message, { type: "success" });
        setShowConfirmSave(false);
        clearErrors();
        onProviderUpdated?.(provider);
        onSuccess?.();
      } else {
        if (!selectedProvider || !dealerId) {
          resetLoadingState();
          return;
        }

        const { provider, message, additionalStep } = await executeAddSubmit({
          dealerId,
          selectedProvider,
          getValues,
        });

        setShowReview(false);
        onReviewModeChange?.(false);

        const hasAdditionalStep =
          additionalStep && hasProviderAdditionalStep(additionalStep);

        if (!hasAdditionalStep) {
          setShowSuccessMessage(true);
        }

        toast(message, { type: "success" });
        setShowConfirmSave(false);
        clearErrors();
        onSuccess?.(provider);
      }
    } catch (error: unknown) {
      const defaultErrorMessage = getErrorMessageForMode(mode, showReview);
      const errorMessage = getErrorMessage(error, defaultErrorMessage);
      toast(errorMessage, { type: "error" });
      setShowConfirmSave(false);
    } finally {
      setIsLoading(false);
      setIsSaving(false);
    }
  }, [
    mode,
    showReview,
    dealerId,
    selectedProvider,
    existingProvider,
    dealerProviders,
    getValues,
    clearErrors,
    setIsLoading,
    setShowReview,
    setShowConfirmSave,
    setShowSuccessMessage,
    onReviewModeChange,
    onSuccess,
    onProviderUpdated,
    resetLoadingState,
  ]);

  const handlePrimaryAction = useCallback(async () => {
    if (showReview) {
      setShowConfirmSave(true);
      return;
    }

    await handleSubmit(onSubmit, (err) => {
      const filteredErrors = filterSchemaBasedErrors(
        err,
        selectedProvider?.schema,
      );
      setFormErrors(filteredErrors);
    })();
  }, [
    showReview,
    handleSubmit,
    onSubmit,
    setFormErrors,
    setShowConfirmSave,
    selectedProvider?.schema,
  ]);

  const handleConfirmSave = useCallback(async () => {
    await handleSubmit(onSubmit, (err) => {
      const filteredErrors = filterSchemaBasedErrors(
        err,
        selectedProvider?.schema,
      );
      setFormErrors(filteredErrors);
      if (Object.keys(filteredErrors).length > 0) {
        setShowConfirmSave(false);
      }
    })();
  }, [
    handleSubmit,
    onSubmit,
    setFormErrors,
    setShowConfirmSave,
    selectedProvider?.schema,
  ]);

  return {
    isSaving,
    onSubmit,
    handlePrimaryAction,
    handleConfirmSave,
  };
}
